import React, { useState, useEffect } from 'react';
import { LayoutDashboard, Bell, BarChart3, Banknote, MapPin, ShieldAlert, FolderOpen, ClipboardList } from 'lucide-react';
import useScrollSpy from '../../hooks/useScrollSpy';
import { sans, coral, useTheme } from '../../tokens';
import { getSectionLayout, resolveOrder, subscribeToSectionLayoutChanges } from '../../data/sectionLayoutStore';

// Canonical list of every possible section in the Investor page.
// The order here is the DEFAULT order when the admin hasn't customized
// anything for a given month. InvestorPage uses this same list to render
// sections in order, and SideNav uses it to build the left rail.
export const SECTION_REGISTRY = [
  { id: 'inv-overview',   label: 'Overview',        icon: LayoutDashboard },
  { id: 'inv-alerts',     label: 'Updates',         icon: Bell },
  { id: 'inv-metrics',    label: 'Financial Overview', icon: BarChart3 },
  { id: 'inv-capital',    label: 'Capital & Ownership', icon: Banknote },
  { id: 'inv-roadmap',    label: 'Roadmap',         icon: MapPin },
  { id: 'inv-risks',      label: 'Risks',           icon: ShieldAlert },
  { id: 'inv-financials', label: 'Monthly Highlights', icon: ClipboardList },
  { id: 'inv-documents',  label: 'Documents',       icon: FolderOpen },
];

export default function SideNav({ companyId, selectedPeriod }) {
  const t = useTheme();
  const [layout, setLayout] = useState(() => getSectionLayout(companyId, selectedPeriod));

  useEffect(() => setLayout(getSectionLayout(companyId, selectedPeriod)), [companyId, selectedPeriod]);
  useEffect(() => subscribeToSectionLayoutChanges(() => {
    setLayout(getSectionLayout(companyId, selectedPeriod));
  }), [companyId, selectedPeriod]);

  const allIds = SECTION_REGISTRY.map(s => s.id);
  const visibleIds = resolveOrder(layout, allIds);
  const visible = visibleIds
    .map(id => SECTION_REGISTRY.find(s => s.id === id))
    .filter(Boolean);

  const [activeId, forceActive] = useScrollSpy(visibleIds);

  return (
    <div style={{
      position: 'fixed',
      left: 0,
      top: 0,
      width: 140,
      height: '100vh',
      overflowY: 'auto',
      background: t.bgCard,
      borderRight: '1px solid ' + t.border,
      zIndex: 10,
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      padding: '140px 8px 0 8px',
    }}>
      {visible.map(s => {
        const Icon = s.icon;
        const isActive = activeId === s.id;
        return (
          <div
            key={s.id}
            onClick={() => {
              forceActive(s.id);
              const el = document.getElementById(s.id);
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: isActive ? 600 : 400,
              fontFamily: sans,
              background: isActive ? coral : 'transparent',
              color: isActive ? '#FFF8F1' : t.textMuted,
              borderLeft: isActive ? '3px solid ' + coral : '3px solid transparent',
              transition: 'background 0.15s, color 0.15s',
            }}
          >
            <Icon size={14} />
            {s.label}
          </div>
        );
      })}

      {/* Bottom branding */}
      <div style={{ marginTop: 'auto', padding: '16px 12px 20px', borderTop: '1px solid ' + t.border }}>
        <div style={{ fontSize: 9, fontWeight: 600, color: t.textMuted, fontFamily: sans }}>
          Demo Medical Devices
        </div>
        <div style={{ fontSize: 8, color: t.textFaint, fontFamily: sans, marginTop: 2 }}>
          Confidential -- Investor Use Only
        </div>
      </div>
    </div>
  );
}
